import { orderBy } from 'lodash'

import { F0Dialog } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { CreateProjectMutation, CreateProjectMutationVariables } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { HandleMutationResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type CreateProjectPayload = Omit<
  CreateProjectMutation['projectManagementMutations']['createProjectmanagementProject'],
  'project'
> & {
  project?: Partial<
    CreateProjectMutation['projectManagementMutations']['createProjectmanagementProject']['project']
  > | null
}

type CreateProjectModalForm = {
  name: string
}

type Props = {
  clientId?: string
  create: (dto: CreateProjectMutationVariables) => HandleMutationResult<CreateProjectPayload>
  isOpen: boolean
  onClose: () => void
  onSuccess: (projectId: string) => void | Promise<void>
}

export const CreateProjectModal = ({ clientId, create, isOpen, onClose, onSuccess }: Props) => {
  const { company, employee } = useCurrentSession()

  const mainLegalEntityId = orderBy(company.legalEntities, 'main', 'desc')[0]?.id

  const form = useForm<CreateProjectModalForm>({
    defaultValues: {
      name: '',
    },
    validate: ({ name }) => {
      const errors: FormErrors<CreateProjectModalForm> = {}
      if (!name.trim()) {
        errors.name = i18n.t('project_management.modals.error_messages.mandatory')
      }
      return errors
    },
    onSubmit: async ({ name }) => {
      if (!employee || !mainLegalEntityId) {
        return {
          success: false,
          errors: [
            {
              __typename: 'SimpleError' as const,
              type: 'generic_error',
              message: i18n.t('errors.generic_error'),
            },
          ],
        }
      }

      const result = await create({
        name: name.trim(),
        isBillable: true,
        legalEntityId: mainLegalEntityId,
        projectAdmins: [employee.id],
        projectManagers: [],
        projectViewers: [],
        status: 'active',
        ...(clientId ? { clientId } : {}),
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors(errors)
      })

      if (handled.success && handled.data.project?.id) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onSuccess(handled.data.project.id)
      }

      return handled
    },
  })

  if (!employee || !mainLegalEntityId) return null

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={() => {
        if (form.isSubmitting) return
        onClose()
      }}
      width="sm"
      title={i18n.t('project_management.modals.create_project.title')}
      primaryAction={{
        label: i18n.t('project_management.modals.create_project.submit'),
        onClick: () => form.handleSubmit(),
        disabled: form.isSubmitting || !form.values.name.trim(),
        loading: form.isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: () => {
          if (form.isSubmitting) return
          onClose()
        },
        disabled: form.isSubmitting,
      }}
    >
      <Input
        label={i18n.t('project_management.modals.create_project.input_labels.project_name')}
        value={form.values.name}
        onChange={(value) => form.setFieldValue('name', value)}
        error={form.errors.name}
        size="md"
        autoFocus
      />
    </F0Dialog>
  )
}
